import { type RegisteredRouter, useRouterState } from '@tanstack/react-router';
import type { FileRouteTypes } from '@/routeTree.gen';

/**
 * The pages the cookie consent banner is not allowed to open on.
 *
 * The legal notice, the privacy policy and the cookie policy — on a portal and on the landing alike, for
 * the two of the three a portal serves. These are pages a donor is reading rather than acting on, and the
 * banner opens as a centred modal, so it sits on top of the text. They are also where the banner's own
 * "Privacy policy", "Legal notice" and "More information" links lead, which makes covering the page the
 * expected outcome of following them rather than an unlucky one — the cookie policy most of all, since it
 * is the page a donor opens precisely to work out what they are being asked.
 *
 * The banner is still reachable from every one of them: the footer's "Privacy settings" opens the settings
 * layer, which is the layer that can actually answer the question. Only the first layer is withheld. See
 * `syncUsercentricsBannerVisibility`.
 *
 * Route ids rather than paths: every one of these pages is served under a translated slug per locale
 * (`/politica-de-privacidad`, `/informativa-sulla-privacy`, …), and matching those by hand would silently
 * stop covering a locale the moment one is added. Typed as generated route ids so renaming a route file
 * breaks the build here instead of quietly re-admitting the banner.
 */
const BANNER_FREE_ROUTE_IDS: readonly FileRouteTypes['id'][] = [
	'/$portalId/legal-disclaimer',
	'/$portalId/privacy-policy',
	'/www/cookies',
	'/www/legal-disclaimer',
	'/www/privacy-policy',
];

/**
 * Whether the page currently rendered is one of {@link BANNER_FREE_ROUTE_IDS}.
 *
 * Read from the router's matches rather than from the URL, so it follows client-side navigation: entering
 * and leaving these pages is what the consent banner has to react to. Used only from effects, never in the
 * markup, so it cannot make the server and the client disagree about what to render.
 */
export const useBannerFreeRoute = (): boolean =>
	useRouterState<RegisteredRouter, boolean>({
		select: (state) => state.matches.some((match) => BANNER_FREE_ROUTE_IDS.some((id) => id === match.routeId)),
	});
